import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import _debounce from 'lodash/debounce';

import SearchInput from '../../../../src-new/components/inputs/SearchInput';
import { searchWorkspace, isTicketOpen } from '../../../../actions';
import { Spin } from 'antd';
import UserAvatar from '../../atoms/ticket-drawer/UserAvatar';
import { FormattedMessage } from 'react-intl';

const PILLS = [
  { id: 1, labelId: 'TASKS',    defaultLabel: 'Tasks'    },
  { id: 3, labelId: 'PROJECTS', defaultLabel: 'Projects' },
  { id: 4, labelId: 'MEMBERS',  defaultLabel: 'People'   },
  { id: 5, labelId: 'FORMS',    defaultLabel: 'Forms'    },
];

const RECENT_TASKS_KEY = 'workflo_recent_tasks';

const SectionHeader = ({ label }) => (
  <div className="px-4 pt-3 pb-1">
    <span className="text-[10.5px] font-semibold uppercase tracking-widest" style={{ color: 'rgba(99,102,241,0.6)' }}>
      {label}
    </span>
  </div>
);

const ResultRow = ({ left, right, onClick }) => (
  <div
    className="flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors rounded-lg mx-2 mb-0.5"
    style={{ color: 'rgba(15,15,25,0.8)' }}
    onClick={onClick}
    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.07)'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
  >
    {left}
    {right && <span className="text-[11px] flex-shrink-0 ml-3 font-mono" style={{ color: 'rgba(99,102,241,0.55)' }}>{right}</span>}
  </div>
);

const WorkspaceSearchBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const queryParams = new URLSearchParams(location.search);
  const initialSearchTerm = queryParams.get('query') || '';

  const isSearchPage = window.location?.pathname === '/search';

  const [searchInput, setSearchInput] = useState(initialSearchTerm);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPill, setSelectedPill] = useState(0);
  const [taskResults, setTaskResults] = useState([]);
  const [projectResults, setProjectResults] = useState([]);
  const [membersResults, setMembersResults] = useState([]);
  const [formResults, setFormResults] = useState([]);

  const { search } = useSelector((state) => state.workspaceReducer);
  const myProjects = useSelector((state) => state.projectReducer.myProjects) || [];
  const searchLoading = search?.isLoading;
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const recentTasks = (() => {
    try { return JSON.parse(localStorage.getItem(RECENT_TASKS_KEY) || '[]').slice(0, 4); } catch { return []; }
  })();

  useEffect(() => {
    setTaskResults(search?.results?.tasks || []);
    setProjectResults(search?.results?.projects || []);
    setMembersResults(search?.results?.users || []);
    setFormResults(search?.results?.forms || []);
  }, [search?.results]);

  const doSearch = useCallback(
    _debounce((value) => {
      if (value.trim()) dispatch(searchWorkspace(value, 'popover'));
    }, 350),
    [],
  );

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchInput(value);
    setShowDropdown(true);
    doSearch(value);
  };

  const handleFocus = () => {
    if (!isSearchPage) setShowDropdown(true);
  };

  const handleShowAll = () => {
    const typeMap = { 1: 'task', 2: 'messages', 3: 'project', 4: 'member' };
    const params = new URLSearchParams(searchParams);
    params.set('type', typeMap[selectedPill] || 'task');
    params.set('query', searchInput.trim());
    navigate(`/search?${params.toString()}`);
    setShowDropdown(false);
  };

  const openTask = (task) => {
    const params = new URLSearchParams(location.search);
    params.delete('msgId');
    params.set('selectedTask', task?.titleIdentifier);
    navigate(`${location.pathname}?${params.toString()}`);
    dispatch(isTicketOpen(true));
    closeDropdown();
  };

  const closeDropdown = () => { setShowDropdown(false); setSearchInput(''); };

  const hasQuery = searchInput.trim().length > 0;
  const noResults = hasQuery && taskResults?.length === 0 && projectResults?.length === 0 && membersResults?.length === 0 && formResults?.length === 0;

  const showTasks    = selectedPill === 1 || selectedPill === 0;
  const showProjects = selectedPill === 3 || selectedPill === 0;
  const showMembers  = selectedPill === 4 || selectedPill === 0;
  const showForms    = selectedPill === 5 || selectedPill === 0;
  const taskSlice    = selectedPill === 1 ? 8 : 3;
  const projSlice    = selectedPill === 3 ? 8 : 3;
  const memberSlice  = selectedPill === 4 ? 8 : 3;
  const formSlice    = selectedPill === 5 ? 8 : 3;

  // Close on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      const inContainer = containerRef.current && containerRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inContainer && !inDropdown) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const EmptyDropdown = () => (
    <>
      {recentTasks.length > 0 && (
        <>
          <SectionHeader label="Recents" />
          {recentTasks.map((task, i) => (
            <ResultRow
              key={task._id || i}
              onClick={() => openTask(task)}
              left={
                <div className="flex items-center gap-2 min-w-0">
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="rgba(99,102,241,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <circle cx="8" cy="8" r="6.5" /><path d="M8 5v3.2l2 2" />
                  </svg>
                  <span className="text-[13px] truncate max-w-[75%]" style={{ color: 'rgba(15,15,25,0.8)' }}>{task.taskTitle}</span>
                </div>
              }
              right={task.titleIdentifier}
            />
          ))}
        </>
      )}

      {myProjects.slice(0, 4).length > 0 && (
        <>
          <SectionHeader label="Projects" />
          {myProjects.slice(0, 4).map((project, i) => (
            <ResultRow
              key={project._id || i}
              onClick={() => { navigate(`/${project._id}/board`); closeDropdown(); }}
              left={
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-4 h-4 rounded flex-shrink-0" style={{ background: project?.color || 'rgba(99,102,241,0.2)' }} />
                  <span className="text-[13px] truncate" style={{ color: 'rgba(15,15,25,0.8)' }}>{project?.title}</span>
                </div>
              }
            />
          ))}
        </>
      )}

      <div className="px-4 py-2.5 mt-1" style={{ borderTop: '1px solid rgba(99,102,241,0.07)' }}>
        <p className="text-[11px]" style={{ color: 'rgba(15,15,25,0.35)' }}>
          Search tasks, projects, and members across your workspace
        </p>
      </div>
    </>
  );

  // Compute dropdown position from the container's bounding rect so the portal
  // can be anchored to the search bar even though it renders on document.body.
  const [dropdownRect, setDropdownRect] = useState(null);
  useEffect(() => {
    if (showDropdown && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownRect(rect);
    }
  }, [showDropdown]);

  const dropdown = showDropdown && !isSearchPage && dropdownRect ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="overflow-hidden"
      style={{
        position: 'fixed',
        top: dropdownRect.bottom + 8,
        left: dropdownRect.left,
        width: Math.max(420, dropdownRect.width),
        zIndex: 99999,
        borderRadius: 16,
        background: 'white',
        border: '1px solid rgba(99,102,241,0.14)',
        boxShadow: '0 20px 60px rgba(15,15,25,0.14), 0 4px 20px rgba(99,102,241,0.1)',
      }}
    >
          {/* Filter pills */}
          <div className="flex items-center gap-1.5 px-4 pt-3 pb-2.5" style={{ borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
            {PILLS.map((pill) => {
              const active = selectedPill === pill.id;
              return (
                <button
                  key={pill.id}
                  onClick={() => setSelectedPill(active ? 0 : pill.id)}
                  className="flex items-center gap-1 h-6 px-3 rounded-full text-xs font-medium transition-all"
                  style={active
                    ? { background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', color: '#4f46e5' }
                    : { background: 'transparent', border: '1px solid rgba(99,102,241,0.15)', color: 'rgba(15,15,25,0.5)' }
                  }
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(99,102,241,0.05)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <FormattedMessage id={pill.labelId} defaultMessage={pill.defaultLabel} />
                  {active && (
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="ml-0.5">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  )}
                </button>
              );
            })}
          </div>

          {/* Results / Empty state */}
          <div className="py-1 max-h-[60vh] overflow-y-auto">
            {!hasQuery ? (
              <EmptyDropdown />
            ) : searchLoading ? (
              <div className="flex items-center justify-center py-10">
                <Spin size="default" />
              </div>
            ) : noResults ? (
              <div className="flex flex-col items-center py-10 text-center px-6">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3" style={{ background: 'rgba(99,102,241,0.07)' }}>
                  <img src="/images/icons/search.svg" className="w-5 h-5 opacity-40" alt="" />
                </div>
                <p className="text-sm font-semibold" style={{ color: 'rgba(15,15,25,0.55)' }}>No results found</p>
                <p className="text-xs mt-1" style={{ color: 'rgba(15,15,25,0.35)' }}>Try different keywords or adjust filters</p>
              </div>
            ) : (
              <>
                {showTasks && taskResults?.slice(0, taskSlice).map((task, i) => (
                  <React.Fragment key={task._id || i}>
                    {i === 0 && <SectionHeader label={<FormattedMessage id="TASKS" defaultMessage="Tasks" />} />}
                    <ResultRow
                      onClick={() => openTask(task)}
                      left={
                        <span className="text-sm truncate max-w-[75%]" style={{ color: 'rgba(15,15,25,0.8)' }}>
                          {task.taskTitle}
                        </span>
                      }
                      right={task.titleIdentifier}
                    />
                  </React.Fragment>
                ))}

                {showProjects && projectResults?.slice(0, projSlice).map((project, i) => (
                  <React.Fragment key={project._id || i}>
                    {i === 0 && <SectionHeader label={<FormattedMessage id="PROJECTS" defaultMessage="Projects" />} />}
                    <ResultRow
                      onClick={() => { navigate(`/${project?._id}/board`); setShowDropdown(false); }}
                      left={
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm truncate" style={{ color: 'rgba(15,15,25,0.8)' }}>{project?.title}</span>
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-md capitalize flex-shrink-0 font-medium"
                            style={{ background: project?.access === 'public' ? 'rgba(34,197,94,0.1)' : 'rgba(99,102,241,0.1)', color: project?.access === 'public' ? '#16a34a' : '#4f46e5' }}
                          >
                            {project?.access}
                          </span>
                          {project?.archived && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md flex-shrink-0 font-medium" style={{ background: 'rgba(15,15,25,0.08)', color: 'rgba(15,15,25,0.5)' }}>
                              Archived
                            </span>
                          )}
                        </div>
                      }
                    />
                  </React.Fragment>
                ))}

                {showMembers && membersResults?.slice(0, memberSlice).map((member, i) => (
                  <React.Fragment key={member._id || i}>
                    {i === 0 && <SectionHeader label={<FormattedMessage id="MEMBERS" defaultMessage="People" />} />}
                    <ResultRow
                      onClick={() => { navigate(`/profile/${member?._id}`); setShowDropdown(false); }}
                      left={
                        <div className="flex items-center gap-2 min-w-0">
                          <UserAvatar classNames="h-5 w-5 rounded-full flex-shrink-0" fullName={member?.fullName} userId={member?._id} src={member?.avi} />
                          <span className="text-sm truncate" style={{ color: 'rgba(15,15,25,0.8)' }}>{member?.fullName}</span>
                        </div>
                      }
                      right={member?.email}
                    />
                  </React.Fragment>
                ))}

                {showForms && formResults?.slice(0, formSlice).map((form, i) => (
                  <React.Fragment key={form._id || i}>
                    {i === 0 && <SectionHeader label={<FormattedMessage id="FORMS" defaultMessage="Forms" />} />}
                    <ResultRow
                      onClick={() => { navigate(`/forms?formId=${form._id}&formView=submissions`); setShowDropdown(false); }}
                      left={
                        <div className="flex items-center gap-2 min-w-0">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                            <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>
                          </svg>
                          <span className="text-sm truncate" style={{ color: 'rgba(15,15,25,0.8)' }}>{form?.formTitle}</span>
                        </div>
                      }
                    />
                  </React.Fragment>
                ))}
              </>
            )}
          </div>

          {/* View all — only when there's a query */}
          {hasQuery && (
            <div style={{ borderTop: '1px solid rgba(99,102,241,0.08)' }}>
              <button
                onClick={handleShowAll}
                className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors"
                style={{ color: 'rgba(99,102,241,0.75)', background: 'transparent' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.05)'; e.currentTarget.style.color = '#4f46e5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(99,102,241,0.75)'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <FormattedMessage id="VIEW_ALL_RESULTS" defaultMessage="View all results" />
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="ml-auto opacity-50">
                  <path d="M2 5h6M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: '1px solid rgba(99,102,241,0.06)', background: 'rgba(99,102,241,0.015)' }}>
            <span className="text-[11px]" style={{ color: 'rgba(15,15,25,0.4)' }}>
              <FormattedMessage id="NOT_EXPECTED_RESULT" defaultMessage="Not the results that you expected?" />
            </span>
            <button
              onClick={() => window.open('https://workflo.canny.io/bugs-and-feature-requests', '_blank')}
              className="text-[11px] font-medium transition-colors"
              style={{ color: 'rgba(99,102,241,0.7)' }}
              onMouseEnter={e => e.currentTarget.style.color = '#4f46e5'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(99,102,241,0.7)'}
            >
              <FormattedMessage id="GIVE_FEEDBACK" defaultMessage="Give Feedback" />
            </button>
          </div>
        </div>,
    document.body,
  ) : null;

  return (
    <div className="relative" ref={containerRef}>
      <SearchInput
        value={searchInput}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onClick={() => { if (!isSearchPage) setShowDropdown(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter') handleShowAll(); if (e.key === 'Escape') { setShowDropdown(false); } }}
      />
      {dropdown}
    </div>
  );
};

export default WorkspaceSearchBar;
